'use client'

import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import LocationAutocomplete from './LocationAutocomplete'
import FiltersContent, { getSearchBarIcons } from './SpaceTypeFilters'
import { SearchFilters } from './types'
import { useSearchBar } from './useSearchBar'

interface MobileSearchBarProps {
  onSearch?: (filters: SearchFilters) => void
}

const MobileSearchBar = ({ onSearch }: MobileSearchBarProps) => {
  const { t } = useTranslation('common')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [expandedSection, setExpandedSection] = useState<
    'where' | 'what' | null
  >('where')

  useEffect(() => {
    if (isModalOpen) {
      setExpandedSection('where')
    }
  }, [isModalOpen])

  const {
    searchFilters,
    handleLocationSelect,
    handleFilterSelect,
    handleResetTypes,
    handleClearAll,
    handleShowSpaces,
    getDisplayText,
    updateSearch
  } = useSearchBar({ onSearch })

  const handleModalClose = () => {
    setIsModalOpen(false)
    setExpandedSection(null)
  }

  const handleLocationSelectAndExpand = (args: {
    search?: string
    placeId?: string
    description?: string
  }) => {
    handleLocationSelect(args)
    setExpandedSection('what')
  }

  const searchBarIcons = getSearchBarIcons(searchFilters.spaceTypes)

  return (
    <>
      {/* Collapsed Search Trigger */}
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="flex w-full items-center gap-2.5 rounded-full border border-ink-100/20 bg-white-500 py-2 pr-3 pl-2.5 shadow-sm transition-[box-shadow,transform] duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 active:scale-[0.99] active:shadow-none"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-cream-300 text-blue-800">
          <i className="si si-magnifying-glass text-[15px]" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 text-left">
          <p className="truncate text-sm leading-tight font-semibold text-ink-800">
            {searchFilters.search || t('SEARCH_BAR.ENTER_ADDRESS')}
          </p>
          <p className="mt-0.5 truncate text-xs leading-tight text-ink-200">
            {getDisplayText()}
          </p>
        </div>
        {searchBarIcons.length > 0 && (
          <div className="flex shrink-0 items-center gap-1 text-ink-200">
            {searchBarIcons.slice(0, 2).map((iconOption, index) => (
              <div key={index} className="flex items-center">
                {iconOption.muiIcon && (
                  <iconOption.muiIcon sx={{ fontSize: 16 }} />
                )}
                {iconOption.icon && (
                  <svg
                    className="size-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d={iconOption.icon}
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </button>

      {/* Full Screen Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[15000] bg-black-500/50">
          <div className="flex h-full flex-col bg-cream-200">
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-ink-100/15 bg-white-500 px-4 pt-4 pb-3">
              <button
                type="button"
                onClick={handleModalClose}
                className="flex size-9 items-center justify-center rounded-full transition-colors hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                aria-label={t('A11Y.CLOSE')}
              >
                <i className="si si-close text-lg text-ink-700" aria-hidden />
              </button>
              <h2 className="text-base font-semibold text-ink-800">
                {t('SEARCH_BAR.SEARCH')}
              </h2>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 pt-4 pb-28">
              {/* Where Section */}
              <div
                className={`mb-3 overflow-hidden rounded-2xl border bg-white-500 transition-colors ${
                  expandedSection === 'where'
                    ? 'border-blue-800/40'
                    : 'border-ink-100/20'
                }`}
              >
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === 'where' ? null : 'where'
                    )
                  }
                  type="button"
                  aria-expanded={expandedSection === 'where'}
                  className="flex w-full items-center justify-between p-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  <span className="text-[10px] font-semibold tracking-[0.18em] text-ink-200 uppercase">
                    {t('SEARCH_BAR.WHERE')}
                  </span>
                  <span className="max-w-[60%] truncate text-sm text-ink-800">
                    {searchFilters.search || t('SEARCH_BAR.SEARCH_ADDRESS')}
                  </span>
                </button>

                {expandedSection === 'where' && (
                  <div className="border-t border-ink-100/15 p-4">
                    <div className="relative">
                      <i
                        className="si si-magnifying-glass absolute top-1/2 left-3.5 -translate-y-1/2 text-[15px] text-ink-200"
                        aria-hidden
                      />
                      {/* Do NOT add `type="text"` — see SearchBar/index.tsx
                          for the React 19 default-attribute hydration bug.
                          The previous inline `style={{ fontSize: '16px' }}`
                          was the iOS-zoom-prevention pattern but also drifted
                          via SSR/CSR whitespace serialization; replaced with
                          Tailwind `text-base` which is the CLAUDE.md
                          canonical fix for "Mobile Input Zoom — 16px Minimum". */}
                      <input
                        value={searchFilters.search}
                        onChange={(e) => updateSearch(e.target.value)}
                        placeholder={t('SEARCH_BAR.SEARCH_ADDRESS')}
                        className="w-full rounded-xl border border-ink-100/20 bg-cream-200 px-10 py-3 text-base text-ink-800 outline-none placeholder:text-ink-200 focus:border-blue-800/60 focus:bg-white-500 focus:ring-2 focus:ring-blue-500/20"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        inputMode="text"
                      />
                      {searchFilters.search && (
                        <button
                          type="button"
                          aria-label={t('A11Y.CLEAR_SEARCH')}
                          onClick={() => updateSearch('')}
                          className="absolute top-1/2 right-3 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-ink-200 hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                        >
                          <i className="si si-close text-xs" aria-hidden />
                        </button>
                      )}
                    </div>
                    <div className="mt-2">
                      <LocationAutocomplete
                        onLocationSelect={(placeId, description) => {
                          handleLocationSelectAndExpand({
                            placeId,
                            description,
                            search: description ?? ''
                          })
                        }}
                        searchValue={searchFilters.search}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* What Section */}
              <div
                className={`mb-3 overflow-hidden rounded-2xl border bg-white-500 transition-colors ${
                  expandedSection === 'what'
                    ? 'border-blue-800/40'
                    : 'border-ink-100/20'
                }`}
              >
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === 'what' ? null : 'what'
                    )
                  }
                  type="button"
                  aria-expanded={expandedSection === 'what'}
                  className="flex w-full items-center justify-between p-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  <span className="text-[10px] font-semibold tracking-[0.18em] text-ink-200 uppercase">
                    {t('SEARCH_BAR.WHAT')}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-ink-200">
                      {searchBarIcons.slice(0, 2).map((iconOption, index) => (
                        <div key={index}>
                          {iconOption.muiIcon && (
                            <iconOption.muiIcon sx={{ fontSize: 16 }} />
                          )}
                          {iconOption.icon && (
                            <svg
                              className="size-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d={iconOption.icon}
                              />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                    <span className="max-w-[120px] truncate text-sm text-ink-800">
                      {getDisplayText()}
                    </span>
                  </div>
                </button>

                {expandedSection === 'what' && (
                  <div className="overflow-hidden border-t border-ink-100/15 p-4">
                    <FiltersContent
                      selectedFilters={searchFilters.spaceTypes}
                      onFilterSelect={handleFilterSelect}
                      onReset={handleResetTypes}
                      onShowSpaces={() => {
                        handleShowSpaces()
                        handleModalClose()
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Bottom Actions */}
            <div className="absolute inset-x-0 bottom-0 border-t border-ink-100/15 bg-white-500 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="rounded text-sm font-medium text-ink-700 underline-offset-4 transition-colors hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  {t('SEARCH_BAR.CLEAR_ALL')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleShowSpaces()
                    handleModalClose()
                  }}
                  className="flex items-center gap-2 rounded-full bg-blue-500 px-7 py-3 text-sm font-semibold text-white-500 shadow-[0_8px_20px_-8px_rgba(28,100,242,0.55)] transition-[background-color,box-shadow,transform] duration-200 hover:bg-blue-600 hover:shadow-[0_10px_24px_-8px_rgba(28,100,242,0.65)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 active:scale-[0.98]"
                >
                  <i
                    className="si si-magnifying-glass text-[15px]"
                    aria-hidden
                  />
                  <span>{t('SEARCH_BAR.SEARCH')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MobileSearchBar
